import { Component, OnInit, ViewEncapsulation } from '@angular/core';

import { QuizService, UserMoteService, MoteEventService, VoucherService, AutoSubOnDestroy, AutoSub } from '../../providers';
import { Mote, Challenge, MoteEventType, MoteEvent, MoteType } from '../../models';
import { MotesLoginService } from '../../motes-login';

/**
 * Displays `Challenge` information in a popup when receiving `QUIZ_COMPLETED` MoteEvents
 */
@AutoSubOnDestroy()
@Component({
    selector: 'challenge-popup',
    templateUrl: './challenge-popup.component.html',
    styleUrls: ['./challenge-popup.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ChallengePopupComponent implements OnInit {
    /** Flag to determine whether to show the popup */
    public showModal: boolean = false;
    /** The `Mote` associated with the latest `QUIZ_COMPLETED` MoteEvent */
    private mote: Mote = null;
    /** The `Challenge` associated with the latest `QUIZ_COMPLETED` MoteEvent */
    public challenge: Challenge = null;
    /** The amount of points the current user has in the `Challenge` associated with the latest `QUIZ_COMPLETED` MoteEvent */
    public userPoints: number = 0;
    /** Flag to determine whether the current user has completed the `Challenge` associated with the latest `QUIZ_COMPLETED` MoteEvent */
    public isChallengeCompleted: boolean = false;
    /** Flag to determine whether quest mode is enabled */
    public isQuestMode: boolean = false;

    constructor(
        private quizService: QuizService,
        private userMoteService: UserMoteService,
        private moteEventService: MoteEventService,
        private voucherService: VoucherService,
        private loginService: MotesLoginService
    ) { }

    ngOnInit() {
        // Subscribe to QUIZ_COMPLETED MoteEvents
        AutoSub(this).reg['quizMoteEventSub'] = this.moteEventService.watchEventType(MoteEventType.QUIZ_COMPLETED).subscribe(ev => {
            this.handleEvent(ev);
        });
    }

    /**
     * Handles an incoming MoteEvent:
     * Finds the corresponding Mote and Challenge, detects quest mode, gets user points and sets member variables for determing the view.
     * @param ev the `MoteEvent`
     * @returns a Promise which resolves once the operations have completed
     */
    private async handleEvent(ev: MoteEvent): Promise<void> {
        if(!ev.mote.quiz) return;
        // Update mote
        this.mote = ev.mote;
        // Get challenge
        this.challenge = await this.quizService.getChallenge(ev.mote.quiz.challenge);
        await this.detectQuestMode(this.mote);
        // Update challenge
        this.userPoints = this.quizService.getChallengeState(this.challenge.key).points;
        this.isChallengeCompleted = this.userPoints >= this.challenge.pointGoal;
    }

    /**
     * Detects quest mode and automatically closes the popup
     */
    private async detectQuestMode(mote: Mote): Promise<void> {
        // Get mote parent to see if this is part of a quest or normal quiz
        let parent = await this.userMoteService.getUserMote(mote.parent);
        // Check if challenge parent is a quest        
        this.isQuestMode = (parent && parent.type == MoteType.QUEST);
        // Automatically close and reveal new route in quest mode
        if(this.isQuestMode) {
            setTimeout(() => {
                // Show new quest route on map
                this.nextMote();
            }, 2800);
        }
    }

    /** 
     * Claims the reward for the current Challenge by emitting a `VOUCHER_CLAIMED` MoteEvent 
     */
    public async claimReward() {
        let user = await this.loginService.getCurrentUser();
        if(!user) {
            console.warn("Could not claim reward: Invalid user:", user);
        }

        let voucher = await this.voucherService.claimReward(this.challenge, user.uid, user.email, this.challenge.userPoints);
        if(!voucher) return console.error("Could not claim reward: Database error");
        this.moteEventService.emitMoteEvent({ mote: this.mote, type: MoteEventType.VOUCHER_CLAIMED, options: { voucherKey: voucher.key, productKey: voucher.productKey } });
    }

    /** 
     * Navigates to the next Mote on the current tour by emitting a `NAVIGATE_TO_MOTE` MoteEvent 
     */
    public async nextMote() {
        // Find the next mote
        let mote = await this.userMoteService.getNextMote(this.mote);
        // If there is no next mote (quiz completed, do not emit anything)
        if(!mote) return;
        // Emit MoteEvent
        this.moteEventService.emitMoteEvent({ mote: mote, type: MoteEventType.NAVIGATE_TO_MOTE });
    }

    /** 
     * Closes the popup
     */
    public close() {
        this.showModal = false;
    }
}
